import { colorFacetsNeutralLight200 } from '@netlify/netlify-design-tokens'
import { Heading, IconButton, Pane, Paragraph, SidebarTab, Strong, Tablist, TabNavigation } from 'evergreen-ui'

import Avatar from '~/components/evergreen/Avatar'
import Tooltip from '~/components/evergreen/Tooltip'
import Flex from '~/components/Flex'
import { DragHandleVerticalIcon, DrawerLeftIcon, DrawerRightIcon, SmallSquareIcon } from '~/icons/evergreen'
import { useActions, useStore } from '~/store/hooks'

import CustomIcon from './CustomIcon'

interface Props {
  className?: string
}

const GutterIcon = ({ isSelected, iconName }: { isSelected: boolean; iconName: string }) => (
  <CustomIcon marginRight={12} name={`${iconName}${isSelected ? 'Active' : ''}`} size={14} />
)

export default function HudGutter(props: Props) {
  const user = useStore(state => state.account.user)
  const site = useStore(state => state.netlify.site)
  const permissionsURL = useStore(state => state.ref.permissionsURL)
  const hidden = useStore(state => state.members.hidden)
  const pane = useStore(state => state.hud.pane)
  const badges = useStore(state => state.hud.badges)

  const setPane = useActions(actions => actions.hud.setPane)
  const justify = useActions(actions => actions.hud.justify)

  const switchPane = (slug: string) => {
    setPane(slug)
    if (document.querySelector('#scrollview')) {
      // @ts-ignore
      document.querySelector('#scrollview').scrollTo(0, 0)
    }
  }

  const topItems: any[] = [
    { heading: 'Review' },
    {
      icon: iconProps => <GutterIcon iconName="conversation" {...iconProps} />,
      label: 'Conversation',
      pane: 'activity',
      badge: badges.activity,
    },
    {
      icon: iconProps => <GutterIcon iconName="newIssue" {...iconProps} />,
      label: 'New issue',
      pane: 'ticket',
    },
    {
      icon: iconProps => <GutterIcon iconName="integrations" {...iconProps} />,
      label: 'Integrations',
      pane: 'integrations',
      badge: !!permissionsURL,
    },
    !hidden && { heading: 'Debug' },
    !hidden && {
      icon: iconProps => <GutterIcon iconName="deployLogs" {...iconProps} />,
      label: 'Deploy logs',
      pane: 'buildLogs',
    },
    !hidden && {
      icon: iconProps => <GutterIcon iconName="functions" {...iconProps} />,
      label: 'Function logs',
      pane: 'lambdaLogs',
    },
    !hidden && {
      icon: iconProps => <GutterIcon iconName="deploySettings" {...iconProps} />,
      label: 'Deploy settings',
      pane: 'settings',
    },
    { heading: 'Share' },
    {
      icon: iconProps => <GutterIcon iconName="qrCode" {...iconProps} />,
      label: 'QR code',
      pane: 'qrcode',
    },
    !hidden && {
      icon: iconProps => <GutterIcon iconName="members" {...iconProps} />,
      label: 'Members',
      pane: 'teamMembers',
      badge: badges.members,
    },

    // {
    //   icon: HelpIcon,
    //   label: 'Help and feedback',
    //   pane: 'help',
    // },
  ].filter(Boolean)

  return (
    <Flex
      // alignItems="space-between"
      aria-label="Navigation"
      // @ts-ignore
      background="#ffffff"
      borderBottomLeftRadius={8}
      // @ts-ignore
      borderRightColor={colorFacetsNeutralLight200}
      borderRightStyle="solid"
      borderRightWidth={1}
      className={props.className || 'hud-gutter'}
      flexDirection="column"
      height="100%"
      // @ts-ignore
      is="section"
      minWidth={220}
      overflowX="hidden"
      overflowY="auto"
    >
      <TabNavigation overflowX="auto" paddingLeft="8px" paddingRight="8px" width={220}>
        {topItems.map((item, i) => {
          if (item.heading) {
            return (
              <Heading
                key={item.heading}
                borderTopColor={colorFacetsNeutralLight200}
                borderTopStyle="solid"
                borderTopWidth={i === 0 ? 0 : 1}
                marginTop={i === 0 ? 0 : 8}
                paddingBottom={4}
                paddingLeft={16}
                paddingTop={4}
              >
                {/* {item.heading} */}
              </Heading>
            )
          }
          return (
            <SidebarTab
              key={item.label}
              appearance="secondary"
              aria-label={item.label}
              // @ts-ignore
              background={item.pane === pane ? `${colorFacetsNeutralLight200} !important` : undefined}
              boxShadow="none !important"
              className={['sidebartab', 'badge', item.badge && 'badge-showing'].filter(Boolean).join(' ')}
              fontSize={14}
              isSelected={pane === item.pane}
              lineHeight="24px"
              marginBottom={8}
              marginX={8}
              onSelect={() => switchPane(item.pane)}
              paddingX={8}
            >
              <item.icon isSelected={pane === item.pane} /> {item.label}
            </SidebarTab>
          )
        })}
      </TabNavigation>
      <Pane>
        <Tablist overflowX="auto" width={220}>
          <SidebarTab
            appearance="primary"
            // @ts-ignore
            background={pane === 'user' ? '#ffffff' : undefined}
            boxShadow="none !important"
            className={['sidebartab', pane === 'user' && 'sidebartab-active', 'sidebartab-user']
              .filter(Boolean)
              .join(' ')}
            isSelected={pane === 'user'}
            onSelect={() => switchPane('user')}
          >
            <Flex justifyContent="flex-start">
              <Avatar marginRight={8} name={user.name} size={32} src={user.avatar} />
              <Pane maxWidth={150} overflow="hidden" paddingY={12} textOverflow="ellipsis" whiteSpace="nowrap">
                <Strong>{user.name}</Strong>
                <Paragraph size={300}>{site.accountName}</Paragraph>
              </Pane>
            </Flex>
          </SidebarTab>
        </Tablist>
        <Flex
          borderTopColor={colorFacetsNeutralLight200}
          borderTopStyle="solid"
          borderTopWidth={1}
          justifyContent="space-around"
          width="100%"
        >
          <IconButton appearance="minimal" className="drag-handle" cursor="move" icon={DragHandleVerticalIcon} />
          <Tooltip content="Anchor left">
            <IconButton appearance="minimal" icon={DrawerRightIcon} onClick={() => justify('flex-start')} />
          </Tooltip>
          <Tooltip content="Center">
            <IconButton appearance="minimal" icon={SmallSquareIcon} onClick={() => justify('center')} />
          </Tooltip>
          <Tooltip content="Anchor right">
            <IconButton appearance="minimal" icon={DrawerLeftIcon} onClick={() => justify('flex-end')} />
          </Tooltip>
        </Flex>
      </Pane>
    </Flex>
  )
}
